/**
 * Google OIDC callback landing page. The backend redirects here after a successful
 * /api/auth/google/callback with ?token=<jwt>. We stash it in localStorage and
 * redirect to the index page.
 */

import { Box, Typography } from '@mui/material';
import type { ReactElement } from 'react';
import { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { useAuth } from '@/contexts/AuthContext';
import { apiHelper } from '@/helpers/ApiHelper';
import { useTypewriter } from '@/hooks/useTypewriter';

export function CallbackPage(): ReactElement {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const { displayed } = useTypewriter('connecting…', 50);

  useEffect(() => {
    const token = params.get('token');
    const error = params.get('error');
    if (token) {
      apiHelper.setToken(token);
      void refresh().then(() => navigate('/', { replace: true }));
    } else {
      const msg = error ? `Login failed: ${error}` : 'No token in callback';
      navigate(`/login?error=${encodeURIComponent(msg)}`, { replace: true });
    }
  }, [params, navigate, refresh]);

  return (
    <Box
      role="status"
      aria-label="Completing sign-in"
      sx={{
        minHeight: '100dvh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'background.deeper',
      }}
    >
      <Typography
        sx={{
          fontFamily: '"JetBrains Mono", monospace',
          color: 'primary.main',
          textShadow: '0 0 6px rgba(217, 119, 87, 0.55)',
          fontSize: 16,
        }}
      >
        ▸ {displayed}
      </Typography>
    </Box>
  );
}
